// src/oura/components/client/AccessLoginChip.tsx
//
// Whether the merchant has been given their access login, shown beside
// "View as the merchant" (#4160). Seeing the merchant's screens no longer
// implies the merchant can sign in, so the two facts are told apart.
//
// Read from the onboarding record the Client tab already reads (`login`), not
// from `companyId`: a workspace now exists the moment staff first view as the
// merchant, long before any login is sent. Nothing renders while the record is
// loading, absent or unreadable, and nothing renders on fixtures: an invented
// status is worse than none (absent means unknown, never "not sent").

import { useEffect, useState } from "react";
import { Tag } from "@oura/ui";
import { getOnboarding, isOnboardingLive, type OnboardingLogin } from "@service/aeo/onboarding";
import { dayMonth } from "../../onboarding/derive";

/** The chip's words and tone for a login's state. Exported so the sentence is testable. */
export function accessLoginLabel(login: OnboardingLogin): { text: string; tone: "gray" | "amber" | "blue" | "green" } {
    switch (login.status) {
        case "signed_in":
            return { text: login.signedInAt ? `Signed in ${dayMonth(login.signedInAt)}` : "Signed in", tone: "green" };
        case "sent":
            return { text: login.sentAt ? `Login sent ${dayMonth(login.sentAt)}` : "Login sent", tone: "blue" };
        case "sending":
            // On its way, a few seconds (ourteam-backend#5523): not yet "not sent", and not yet "sent".
            return { text: "Access login sending", tone: "blue" };
        case "send_failed":
            // The email did not go out (ourteam-backend#5411): "not sent" is the honest word.
            return { text: "Access login not sent", tone: "amber" };
        case "not_sent":
            return { text: "Access login not sent", tone: "gray" };
        default:
            // A status this screen does not know: said as unknown, never as "not sent".
            return { text: "Access login status unknown", tone: "gray" };
    }
}

export function AccessLoginChip({ businessId }: { businessId: string }) {
    const [login, setLogin] = useState<OnboardingLogin | null>(null);

    useEffect(() => {
        if (!isOnboardingLive) return;
        let cancelled = false;
        setLogin(null);
        getOnboarding(businessId)
            .then((o) => { if (!cancelled) setLogin(o?.login ?? null); })
            .catch(() => { if (!cancelled) setLogin(null); });
        return () => { cancelled = true; };
    }, [businessId]);

    if (!isOnboardingLive || !login) return null;
    const { text, tone } = accessLoginLabel(login);
    return <Tag tone={tone}>{text}</Tag>;
}
